import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useRestaurant } from "../context/RestaurantContext";
import { resetAllOrders } from "../lib/api";
import AppLayout from "../components/AppLayout";
import CustomerRequestsSection from "../components/CustomerRequestsSection";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { 
  Loader2, 
  Trash2, 
  AlertTriangle, 
  Users, 
  Store, 
  Truck, 
  Package,
  ChevronRight,
  Database,
  Settings2,
  History,
  Building2
} from "lucide-react";

export default function AdminSettingsPage() {
  const { currentRestaurant } = useRestaurant();
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [showFinalConfirm, setShowFinalConfirm] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  
  // Settings
  const [combinedLastOrdered, setCombinedLastOrdered] = useState(() => {
    return localStorage.getItem("combinedLastOrdered") === "true";
  });

  // Save settings to localStorage
  const handleCombinedToggle = (checked) => {
    setCombinedLastOrdered(checked);
    localStorage.setItem("combinedLastOrdered", checked.toString());
    toast.success(checked 
      ? "Last ordered info will now show from all restaurants" 
      : "Last ordered info will show from current restaurant only"
    );
  };

  const handleFirstConfirm = () => {
    setShowResetDialog(false);
    setShowFinalConfirm(true);
  };

  const handleResetAll = async () => {
    setIsResetting(true);
    try {
      const response = await resetAllOrders();
      toast.success(`${response.data.deleted_count} orders deleted successfully`);
      setShowFinalConfirm(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset orders");
    } finally {
      setIsResetting(false);
    }
  };

  const managementLinks = [
    {
      title: "Manage Users",
      description: "Add, edit, or manage user accounts",
      icon: Users,
      href: "/users",
      color: "bg-purple-100 text-purple-700"
    },
    {
      title: "Manage Restaurants",
      description: "Add or edit restaurant locations",
      icon: Store,
      href: "/restaurants",
      color: "bg-amber-100 text-amber-700"
    },
    {
      title: "Manage Vendors",
      description: "Add, edit, or remove suppliers",
      icon: Truck,
      href: "/vendors",
      color: "bg-kale-100 text-kale-700"
    },
    {
      title: "Manage Items",
      description: "Add or edit inventory items",
      icon: Package,
      href: "/items",
      color: "bg-blue-100 text-blue-700"
    },
  ];

  return (
    <AppLayout title="Admin Settings" showBack>
      <div className="p-4 md:p-6 max-w-2xl mx-auto space-y-6">
        
        {/* New Customer Requests (from public sign-up landing page) */}
        <CustomerRequestsSection />

        {/* Order Display Settings */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <History className="w-4 h-4 text-kale-600" />
              Order Display Settings
            </CardTitle>
            <CardDescription>
              Configure how order information is displayed
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Combined Last Ordered Toggle */}
            <div className="flex items-center justify-between p-4 border rounded-lg bg-stone-50/50">
              <div className="flex-1 pr-4">
                <Label className="font-medium text-stone-800 flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-kale-600" />
                  Combined Last Ordered
                </Label>
                <p className="text-sm text-stone-500 mt-1">
                  Show "Last Ordered" info from all restaurants combined. Useful when items are shared between locations.
                </p>
              </div>
              <Switch
                checked={combinedLastOrdered}
                onCheckedChange={handleCombinedToggle}
                data-testid="combined-last-ordered-toggle"
              />
            </div>
          </CardContent>
        </Card>

        {/* Data Management Section */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Settings2 className="w-4 h-4 text-kale-600" />
              Data Management
            </CardTitle>
            <CardDescription>
              Manage your restaurant's core data
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y">
              {managementLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="flex items-center gap-4 p-4 hover:bg-stone-50 transition-colors"
                  data-testid={`link-${link.href.slice(1)}`}
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${link.color}`}>
                    <link.icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-stone-800">{link.title}</p>
                    <p className="text-sm text-stone-500 truncate">{link.description}</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-stone-400" />
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Current Context */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-stone-500">Current Restaurant Context</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <p className="font-semibold text-stone-800">{currentRestaurant?.name || "Not selected"}</p>
            <p className="text-xs text-stone-400 mt-1">
              Danger zone actions below apply to this restaurant only.
            </p>
          </CardContent>
        </Card>

        {/* Danger Zone */}
        <Card className="border-red-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-4 h-4" />
              Danger Zone
            </CardTitle>
            <CardDescription>
              Irreversible actions - proceed with caution
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Reset All Orders */}
            <div className="p-4 border border-red-200 rounded-lg bg-red-50/50">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <h4 className="font-medium text-stone-800 flex items-center gap-2">
                    <Database className="w-4 h-4 text-red-600" />
                    Reset All Orders
                  </h4>
                  <p className="text-sm text-stone-500 mt-1">
                    Permanently delete all orders (pending and finalized) for <span className="font-medium">{currentRestaurant?.name}</span>. This cannot be undone.
                  </p>
                </div>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setShowResetDialog(true)}
                  data-testid="reset-all-orders-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Reset
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* First Warning Dialog */}
      <AlertDialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Reset All Orders?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will delete <span className="font-bold text-red-600">ALL orders</span> (both pending and finalized) for <span className="font-semibold">{currentRestaurant?.name}</span>.
              <br /><br />
              This action is <span className="font-bold">permanent</span> and cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-reset-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleFirstConfirm}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="first-confirm-reset-btn"
            >
              I Understand, Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Final Confirmation Dialog */}
      <AlertDialog open={showFinalConfirm} onOpenChange={setShowFinalConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Final Warning!
            </AlertDialogTitle>
            <AlertDialogDescription className="text-base">
              You are about to <span className="font-bold text-red-600">permanently delete ALL orders</span> for {currentRestaurant?.name}.
              <br /><br />
              <span className="font-bold">This includes:</span>
              <ul className="list-disc ml-5 mt-2 space-y-1">
                <li>All pending orders awaiting review</li>
                <li>All finalized/completed orders</li>
                <li>All order history and receipts</li>
              </ul>
              <br />
              Are you absolutely sure?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-final-reset-btn">No, Go Back</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleResetAll}
              className="bg-red-600 hover:bg-red-700"
              disabled={isResetting}
              data-testid="final-confirm-reset-btn"
            >
              {isResetting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Yes, Delete All Orders
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}
